import React from "react";
import { cn } from "../../utils";

export interface NotificationItemBaseProps {
  id?: string;
  type: string;
  title: string;
  content: string;
  time: string;
  isRead?: boolean;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
  resourceType?: string;
  resourceId?: string;
  isLoading?: boolean;
  onClick?: (
    resourceType?: string,
    resourceId?: string,
    id?: string,
    isRead?: boolean,
  ) => void;
}

export const NotificationItemBase = ({
  id,
  type,
  title,
  content,
  time,
  isRead,
  icon: Icon,
  color,
  resourceType,
  resourceId,
  isLoading,
  onClick,
}: NotificationItemBaseProps) => {
  // Clickable if has onClick handler
  const isClickable = !!onClick;

  const handleClick = () => {
    if (onClick && !isLoading) {
      onClick(resourceType, resourceId, id, isRead);
    }
  };

  return (
    <div
      onClick={handleClick}
      className={cn(
        "group flex gap-4 p-5 border rounded-[16px] mb-3 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_8px_30px_rgb(0,0,0,0.06)] bg-white relative",
        isClickable ? "cursor-pointer" : "cursor-default",
        isLoading && "opacity-75",
        isRead
          ? "border-slate-200/60 hover:border-slate-300/80"
          : "border-primary/25 bg-gradient-to-br from-primary/10 to-white hover:border-primary/40",
      )}
    >
      {!isRead && (
        <div className="absolute top-3 right-3 w-2 h-2 bg-[#dc2626] rounded-full shadow-sm" />
      )}
      <div
        className={cn(
          "w-11 h-11 rounded-[12px] flex items-center justify-center shrink-0 transition-all duration-300 group-hover:scale-105 relative overflow-hidden",
          "backdrop-blur-xl border border-white/40 shadow-[0_8px_32px_0_rgba(0,0,0,0.1)]",
          color,
        )}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent" />
        <Icon className="w-5 h-5 text-white relative z-10 drop-shadow-sm" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2 flex-1 min-w-0 pr-4">
            <span className="text-2xs font-semibold text-slate-500 bg-slate-100 px-2 py-0.5 rounded shrink-0">
              {type}
            </span>
            <h4 className="text-base font-semibold text-slate-900 truncate">
              {title}
            </h4>
          </div>
          <span className="text-xs text-slate-400 whitespace-nowrap font-medium">
            {time}
          </span>
        </div>
        <p className="text-sm text-slate-600/90 line-clamp-2 leading-relaxed">
          {content}
        </p>
      </div>
    </div>
  );
};
